import { useCallback } from 'react';
import { useAIChatContext, ChatMessage } from '../components/AIChat/AIChatContext';

/**
 * useAIChat Hook
 *
 * Provides API integration for the AI Chat feature.
 * Handles:
 * - Sending messages to /api/v2/ai/chat
 * - Rate limit handling (429 errors)
 * - Error states and retry functionality
 * - Session continuity via conversationId
 */

interface AIChatResponse {
  success: boolean;
  response: string;
  conversationId: string;
  tokensUsed: number;
  error?: string;
}

interface SendMessageOptions {
  onSuccess?: (response: AIChatResponse) => void;
  onError?: (error: string) => void;
}

const MAX_MESSAGE_LENGTH = 500;

export function useAIChat() {
  const {
    messages,
    conversationId,
    isLoading,
    error,
    addMessage,
    updateMessage,
    setConversationId,
    setIsLoading,
    setError,
    clearError,
    clearMessages,
  } = useAIChatContext();

  /**
   * Send a message to the AI chat endpoint
   */
  const sendMessage = useCallback(
    async (content: string, options?: SendMessageOptions) => {
      // Validate message length
      if (content.length > MAX_MESSAGE_LENGTH) {
        setError(`Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters allowed.`);
        return null;
      }

      if (content.trim().length === 0) {
        setError('Please enter a message.');
        return null;
      }

      clearError();
      setIsLoading(true);

      // Add user message to chat
      const userMessageId = addMessage({
        role: 'user',
        content: content.trim(),
        status: 'sending',
      });

      // Add placeholder for AI response
      const aiMessageId = addMessage({
        role: 'assistant',
        content: '',
        status: 'sending',
      });

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Please log in to use AI support.');
        }

        const response = await fetch('/api/v2/ai/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: content.trim(),
            conversationId: conversationId || undefined,
          }),
        });

        // Handle rate limiting
        if (response.status === 429) {
          const errorMsg = 'You\'re sending messages too quickly. Please wait a moment and try again.';
          updateMessage(userMessageId, { status: 'error', error: errorMsg });
          updateMessage(aiMessageId, { status: 'error', content: errorMsg, error: errorMsg });
          setError(errorMsg);
          options?.onError?.(errorMsg);
          return null;
        }

        // Handle other non-200 responses
        if (!response.ok) {
          let errorMsg = 'Something went wrong. Please try again.';

          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMsg = errorData.error;
            }
          } catch {
            // Use default error message
          }

          updateMessage(userMessageId, { status: 'error', error: errorMsg });
          updateMessage(aiMessageId, { status: 'error', content: errorMsg, error: errorMsg });
          setError(errorMsg);
          options?.onError?.(errorMsg);
          return null;
        }

        const data: AIChatResponse = await response.json();

        if (!data.success) {
          const errorMsg = data.error || 'Failed to get a response. Please try again.';
          updateMessage(userMessageId, { status: 'error', error: errorMsg });
          updateMessage(aiMessageId, { status: 'error', content: errorMsg, error: errorMsg });
          setError(errorMsg);
          options?.onError?.(errorMsg);
          return null;
        }

        // Update messages with success
        updateMessage(userMessageId, { status: 'sent' });
        updateMessage(aiMessageId, {
          status: 'sent',
          content: data.response,
        });

        // Store conversation ID for session continuity
        if (data.conversationId) {
          setConversationId(data.conversationId);
        }

        options?.onSuccess?.(data);
        return data;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Network error. Please check your connection.';
        updateMessage(userMessageId, { status: 'error', error: errorMsg });
        updateMessage(aiMessageId, { status: 'error', content: errorMsg, error: errorMsg });
        setError(errorMsg);
        options?.onError?.(errorMsg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      conversationId,
      addMessage,
      updateMessage,
      setConversationId,
      setIsLoading,
      setError,
      clearError,
    ]
  );

  /**
   * Retry sending the last failed message
   */
  const retryLastMessage = useCallback(async () => {
    // Find the last user message with an error
    const lastFailedUserMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'user' && msg.status === 'error');

    if (!lastFailedUserMessage) {
      return null;
    }

    // Remove the failed messages
    const failedIndex = messages.findIndex((msg) => msg.id === lastFailedUserMessage.id);
    if (failedIndex !== -1) {
      // Clear from the failed message onwards
      const messagesToKeep = messages.slice(0, failedIndex);
      clearMessages();
      // Re-add the kept messages
      messagesToKeep.forEach((msg) => {
        addMessage({
          role: msg.role,
          content: msg.content,
          status: msg.status,
        });
      });
    }

    // Resend the message
    return sendMessage(lastFailedUserMessage.content);
  }, [messages, sendMessage, clearMessages, addMessage]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('authToken');
  }, []);

  /**
   * Quick action suggestions for new users
   */
  const quickActions = [
    {
      label: "What's my safe to spend?",
      message: "What's my safe to spend?",
    },
    {
      label: 'How am I doing this month?',
      message: 'How am I doing this month?',
    },
    {
      label: 'Help me plan a purchase',
      message: 'Help me plan a purchase',
    },
  ];

  return {
    // State
    messages,
    conversationId,
    isLoading,
    error,
    maxMessageLength: MAX_MESSAGE_LENGTH,
    quickActions,

    // Actions
    sendMessage,
    retryLastMessage,
    clearMessages,
    clearError,
    isAuthenticated,
  };
}

export default useAIChat;
